/**
 * Shared utilities for {@link SchoolYearRow}, {@link SchoolYearCard} and {@link SchoolYearsDataTable}.
 */
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { SchoolYear } from '@/types/school-year.types';
import type { SchoolYearFilters } from './school-year-configs';

/** Where today sits relative to the school-year range. */
type PeriodPhase = 'upcoming' | 'active' | 'ended';

/** Row / card timeline fields derived once from a school year. */
export interface DerivedSchoolYearData {
  startLabel: string;
  endLabel: string;
  /** Progress ratio 0–1 representing current position within the school year. */
  progress: number;
  /** Whether to render the "now" marker on the timeline. */
  showNowMarker: boolean;
  periodPhase: PeriodPhase;
}

/** Upcoming before start, ended after end, otherwise active. */
export function getSchoolYearPeriodPhase(startDate: Date, endDate: Date): PeriodPhase {
  const nowTs = Date.now();
  const startMs = new Date(startDate).getTime();
  const endMs = new Date(endDate).getTime();
  if (nowTs < startMs) return 'upcoming';
  if (nowTs > endMs) return 'ended';
  return 'active';
}

/** Inclusive date-range match used by list filters. */
export function matchesSchoolYearDateRange(date: Date, from?: Date, to?: Date): boolean {
  const d = new Date(date);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

/**
 * Derives all display data shared between {@link SchoolYearCard} and {@link SchoolYearRow}.
 * Extracted to avoid duplicating the date/progress/phase computation.
 */
export function deriveSchoolYearDisplayData(schoolYear: SchoolYear): DerivedSchoolYearData {
  const start = new Date(schoolYear.startDate);
  const end = new Date(schoolYear.endDate);
  const startLabel = format(start, 'dd/MM/yyyy', { locale: fr });
  const endLabel = format(end, 'dd/MM/yyyy', { locale: fr });

  const nowTs = Date.now();
  const startMs = start.getTime();
  const endMs = end.getTime();
  const spanMs = Math.max(1, endMs - startMs);
  const progress = Math.min(1, Math.max(0, (nowTs - startMs) / spanMs));
  const isInCycle = nowTs >= startMs && nowTs <= endMs;
  const showNowMarker = isInCycle;

  return {
    startLabel,
    endLabel,
    progress,
    showNowMarker,
    periodPhase: getSchoolYearPeriodPhase(start, end),
  };
}

/** Map UI filters + pagination to school-year list params. */
export function toSchoolYearListParams(
  filters: SchoolYearFilters,
  page: number,
  pageSize: number,
): {
  page: number;
  pageSize: number;
  statuses?: SchoolYearFilters['statuses'];
  isCurrent?: boolean;
  periodPhases?: SchoolYearFilters['periodPhases'];
} {
  return {
    page,
    pageSize,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    isCurrent: filters.isCurrent ?? undefined,
    periodPhases: filters.periodPhases.length > 0 ? filters.periodPhases : undefined,
  };
}

/** True when any status / phase / date / search filter is set. */
export function hasSchoolYearActiveFilters(filters: SchoolYearFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.isCurrent !== null ||
    filters.periodPhases.length > 0 ||
    !!filters.searchQuery?.trim() ||
    filters.startDateFrom !== undefined ||
    filters.startDateTo !== undefined ||
    filters.endDateFrom !== undefined ||
    filters.endDateTo !== undefined
  );
}

/** Local `YYYY-MM-DD` for {@link DatePicker} filter values. */
export function toSchoolYearFilterDateString(d?: Date): string | undefined {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse a filter start date at local midnight. */
export function parseSchoolYearFilterStartDate(val: string): Date {
  const [year, month, day] = val.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0);
}

/** Parse a filter end date at local end-of-day. */
export function parseSchoolYearFilterEndDate(val: string): Date {
  const [year, month, day] = val.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}
